import * as Sentry from "@sentry/react";

export interface InitRendererSentryOptions {
  /**
   * GlitchTip DSN. When empty the SDK is not initialized — useful in
   * development where errors surface in the console already.
   */
  dsn: string;
  environment: string;
  release?: string;
  /**
   * Optional sample rate for performance tracing. GlitchTip has basic
   * tracing support. Default 0 (errors only).
   */
  tracesSampleRate?: number;
}

let initialized = false;

/**
 * Initialize the Sentry/GlitchTip SDK for the React renderer (works
 * for both the web browser build and the Electron renderer — the
 * same bundle runs in both). Safe to call multiple times; subsequent
 * calls are no-ops.
 */
export function initRendererSentry(opts: InitRendererSentryOptions): void {
  if (initialized) return;
  if (!opts.dsn) {
    // Without a DSN we skip init — no network traffic, no breadcrumbs.
    console.warn(
      "[sentry] VITE_SENTRY_DSN is empty — error reporting disabled",
    );
    return;
  }

  Sentry.init({
    dsn: opts.dsn,
    environment: opts.environment,
    release: opts.release,
    tracesSampleRate: opts.tracesSampleRate ?? 0.01,
    // GlitchTip does not support sessions or replays — strip any
    // default session integration so we don't waste traffic on
    // envelopes the server can't ingest.
    integrations: (defaults) => [
      ...defaults.filter(
        (i) => i.name !== "BrowserSession" && i.name !== "Replay",
      ),
      Sentry.browserTracingIntegration(),
    ],
    // Strip noisy known-benign errors the user can't act on.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
  });

  initialized = true;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const setUser = Sentry.setUser;
export const setTag = Sentry.setTag;
export const addBreadcrumb = Sentry.addBreadcrumb;
